import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../contexts/CurrencyContext';

const OrderInvoice = ({ order, onClose }) => {
  const { formatPrice } = useCurrency();
  const [invoiceSettings, setInvoiceSettings] = useState(null);

  useEffect(() => {
    fetchInvoiceSettings();
  }, []);

  const fetchInvoiceSettings = async () => {
    try {
      const response = await fetch('/api/settings/design');
      const data = await response.json();
      if (data.success && data.data.design) {
        setInvoiceSettings(data.data.design);
      }
    } catch (error) {
      console.error('Error fetching invoice settings:', error);
    }
  };

  if (!order) return null;

  const roundMoney = (value) => {
    const amount = Number(value || 0);
    return Math.round(amount * 100) / 100;
  };

  const shippingAddress = typeof order.shipping_address === 'string' 
    ? JSON.parse(order.shipping_address || '{}')
    : order.shipping_address || {};
  
  const orderItems = Array.isArray(order.items) ? order.items : [];
  const promoDetails = order.promo_details || null;
  const subtotalAmount = roundMoney(
    order.subtotal_amount ?? orderItems.reduce((sum, item) => sum + ((Number(item.price || 0) * Number(item.quantity || 0))), 0)
  );
  const discountTotal = roundMoney(order.discount_total || promoDetails?.discount_amount || 0);
  const shippingAmount = roundMoney(
    order.shipping_amount ?? Math.max(0, Number(order.total_amount || 0) - subtotalAmount + discountTotal)
  );
  const totalBeforeDiscount = roundMoney(order.total_before_discount ?? (subtotalAmount + shippingAmount));
  const finalTotal = roundMoney(order.total_amount || 0);
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="invoice-modal-root fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="invoice-modal-backdrop absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="invoice-print-root relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-lg bg-white shadow-xl">
        {/* Header with Print Button */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center print:hidden">
          <h2 className="text-2xl font-bold text-gray-900">Order Invoice</h2>
          <div className="flex gap-3">
            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print Invoice
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
            >
              Close
            </button>
          </div>
        </div>

        {/* Invoice Content */}
        <div className="p-8 print:p-6">
          {/* Company Header */}
          <div className="mb-8 border-b-2 border-gray-300 pb-6">
            <div className="flex items-center justify-between mb-4">
              {/* Logo */}
              {invoiceSettings?.invoice_logo && (
                <div className="flex-shrink-0">
                  <img
                    src={invoiceSettings.invoice_logo}
                    alt="Company Logo"
                    className="max-h-20 max-w-[200px] object-contain"
                  />
                </div>
              )}
              
              {/* Invoice Title */}
              <div className="flex-1 text-center">
                <h1 className="text-4xl font-bold text-gray-900 mb-2">INVOICE</h1>
                <p className="text-lg text-gray-600">Order #{order.order_number || order.id}</p>
                <p className="text-sm text-gray-500 mt-2">Date: {formatDate(order.created_at)}</p>
                {promoDetails?.code && (
                  <p className="mt-1 text-sm font-medium text-indigo-600">
                    Promo Applied: {promoDetails.code}
                    {promoDetails.discount_percent ? ` (${promoDetails.discount_percent}% off)` : ''}
                  </p>
                )}
              </div>
              
              {/* Spacer for alignment */}
              <div className="flex-shrink-0 w-[200px]"></div>
            </div>
            
            {/* Company Info */}
            {invoiceSettings && (
              <div className="mt-4 text-left">
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  {invoiceSettings.invoice_company_name || 'Company Name'}
                </h2>
                <div className="text-sm text-gray-600 space-y-1">
                  {invoiceSettings.invoice_company_address && (
                    <p>{invoiceSettings.invoice_company_address}</p>
                  )}
                  <p>
                    {[
                      invoiceSettings.invoice_company_city,
                      invoiceSettings.invoice_company_state,
                      invoiceSettings.invoice_company_zip
                    ].filter(Boolean).join(', ')}
                    {invoiceSettings.invoice_company_country && `, ${invoiceSettings.invoice_company_country}`}
                  </p>
                  {invoiceSettings.invoice_company_phone && (
                    <p>Phone: {invoiceSettings.invoice_company_phone}</p>
                  )}
                  {invoiceSettings.invoice_company_email && (
                    <p>Email: {invoiceSettings.invoice_company_email}</p>
                  )}
                  {invoiceSettings.invoice_company_website && (
                    <p>Website: {invoiceSettings.invoice_company_website}</p>
                  )}
                  {invoiceSettings.invoice_tax_id && (
                    <p>Tax ID: {invoiceSettings.invoice_tax_id}</p>
                  )}
                  {invoiceSettings.invoice_gst_number && (
                    <p>GST: {invoiceSettings.invoice_gst_number}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Billing & Shipping Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            {/* Billing To */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b border-gray-200 pb-2">
                Bill To:
              </h3>
              <div className="space-y-1 text-sm">
                <p className="font-semibold text-gray-900">
                  {order.customer_name || shippingAddress.first_name + ' ' + (shippingAddress.last_name || '')}
                </p>
                <p className="text-gray-600">{order.email || shippingAddress.email || 'N/A'}</p>
                <p className="text-gray-600">{order.phone || shippingAddress.phone || 'N/A'}</p>
              </div>
            </div>

            {/* Ship To */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b border-gray-200 pb-2">
                Ship To:
              </h3>
              <div className="space-y-1 text-sm">
                <p className="font-semibold text-gray-900">
                  {shippingAddress.first_name || ''} {shippingAddress.last_name || ''}
                </p>
                <p className="text-gray-600">{shippingAddress.street || shippingAddress.address || ''}</p>
                <p className="text-gray-600">
                  {shippingAddress.city || ''}, {shippingAddress.state || ''} {shippingAddress.zip_code || shippingAddress.zipCode || ''}
                </p>
                <p className="text-gray-600">{shippingAddress.country || ''}</p>
                <p className="text-gray-600">Phone: {shippingAddress.phone || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Order Items Table */}
          <div className="mb-8">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900">
                    Item
                  </th>
                  <th className="border border-gray-300 px-4 py-3 text-center text-sm font-semibold text-gray-900">
                    Quantity
                  </th>
                  <th className="border border-gray-300 px-4 py-3 text-right text-sm font-semibold text-gray-900">
                    Unit Price
                  </th>
                  <th className="border border-gray-300 px-4 py-3 text-right text-sm font-semibold text-gray-900">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {orderItems.map((item, index) => (
                  <tr key={item.id || index} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-4 py-3">
                      <div>
                        <p className="font-semibold text-gray-900">{item.product_name || item.name || 'N/A'}</p>
                        {item.sku && (
                          <p className="text-xs text-gray-500">SKU: {item.sku}</p>
                        )}
                      </div>
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-center text-gray-700">
                      {item.quantity || 0}
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-right text-gray-700">
                      {formatPrice(item.price || 0)}
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-right font-semibold text-gray-900">
                      {formatPrice((item.price || 0) * (item.quantity || 0))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end mb-8">
            <div className="w-full md:w-96 space-y-2">
              <div className="flex justify-between text-sm border-b border-gray-200 pb-2">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-semibold text-gray-900">
                  {formatPrice(subtotalAmount)}
                </span>
              </div>
              <div className="flex justify-between text-sm border-b border-gray-200 pb-2">
                <span className="text-gray-600">Delivery Charge:</span>
                <span className="font-semibold text-gray-900">
                  {shippingAmount > 0 ? formatPrice(shippingAmount) : 'Free'}
                </span>
              </div>
              {discountTotal > 0 && (
                <div className="flex justify-between text-sm border-b border-gray-200 pb-2">
                  <span className="text-gray-600">
                    Discount{promoDetails?.code ? ` (${promoDetails.code})` : ''}:
                  </span>
                  <span className="font-semibold text-red-600">
                    -{formatPrice(discountTotal)}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-sm border-b border-gray-200 pb-2">
                <span className="text-gray-600">Total Before Discount:</span>
                <span className="font-semibold text-gray-900">
                  {formatPrice(totalBeforeDiscount)}
                </span>
              </div>
              {order.tax_total > 0 && (
                <div className="flex justify-between text-sm border-b border-gray-200 pb-2">
                  <span className="text-gray-600">Tax:</span>
                  <span className="font-semibold text-gray-900">
                    {formatPrice(order.tax_total || 0)}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold border-t-2 border-gray-400 pt-2">
                <span className="text-gray-900">Total Amount:</span>
                <span className="text-indigo-600">
                  {formatPrice(finalTotal)}
                </span>
              </div>
            </div>
          </div>

          {/* Payment & Order Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 pt-6 border-t border-gray-300">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Payment Information</h4>
              <div className="space-y-1 text-sm text-gray-600">
                <p>Method: <span className="font-semibold text-gray-900 capitalize">{order.payment_method || 'N/A'}</span></p>
                <p>Status: <span className={`font-semibold ${
                  order.payment_status === 'PAID' ? 'text-green-600' : 
                  order.payment_status === 'UNPAID' ? 'text-yellow-600' : 
                  'text-gray-900'
                }`}>{order.payment_status || 'N/A'}</span></p>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Order Information</h4>
              <div className="space-y-1 text-sm text-gray-600">
                <p>Status: <span className="font-semibold text-gray-900 capitalize">{order.status || 'N/A'}</span></p>
                <p>Delivery Type: <span className="font-semibold text-gray-900 capitalize">{order.delivery_type || 'N/A'}</span></p>
                {promoDetails?.code && (
                  <p>
                    Promotion: <span className="font-semibold text-gray-900">{promoDetails.code}</span>
                    {promoDetails.tier ? ` (Tier ${promoDetails.tier})` : ''}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center text-xs text-gray-500 border-t border-gray-300 pt-6 mt-8 space-y-2">
            {invoiceSettings?.invoice_footer_text && (
              <p>{invoiceSettings.invoice_footer_text}</p>
            )}
            {invoiceSettings?.invoice_terms && (
              <div className="mt-4 text-left">
                <p className="font-semibold text-gray-700 mb-1">Terms & Conditions:</p>
                <p className="text-gray-600">{invoiceSettings.invoice_terms}</p>
              </div>
            )}
            <p className="mt-4">This is a computer-generated invoice. No signature required.</p>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden !important;
          }
          .invoice-modal-root,
          .invoice-modal-root * {
            visibility: visible !important;
          }
          .invoice-modal-root {
            position: static !important;
            display: block !important;
            padding: 0 !important;
            background: white !important;
          }
          .invoice-modal-backdrop {
            display: none !important;
          }
          .invoice-print-root {
            position: static !important;
            inset: auto !important;
            width: 100% !important;
            max-width: 100% !important;
            max-height: none !important;
            overflow: visible !important;
            margin: 0 !important;
            border: none !important;
            border-radius: 0 !important;
            box-shadow: none !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:p-6 {
            padding: 1.5rem !important;
          }
          .bg-white {
            background: white !important;
            box-shadow: none !important;
            border: none !important;
          }
          @page {
            margin: 12mm;
          }
        }
      `}</style>
    </div>
  );
};

export default OrderInvoice;

